import { runInAction } from 'mobx';
import { useRef, useEffect } from 'react';
import { useMemoOne } from 'use-memo-one';

export function useAppManager(
  MgrCls,
  parentMgr,
  initVars = {},
  create = true,
  dependencies = [],
) {
  /* A hook to conditionally loads an appManger. 
    We pass the 'create' boolean here so the hook can be called at the top-level (not inside a condition)
  
    Appmanagers should ideally specify their required parameters and types, as well as other AppManagers they depend on
    
    An appManager might change state (including based on requests made) and thereby have the 
    component select a different sub-component and therefore load different sub-managers
    Each top-level (where appManager is mounts) React Component is effectively a self contained application,
    potentially with sub applications
  
    We wrap the creation of the Mgr in runInAction for two reasons:
      1. For mobx 6 compatability where actions are enforced.
      2. So that this will still work when run in a reaction though it should not be called in a reaction.
      => We *should not() wrap the useAppManager call in an observer but use the useObserver hook 
      or not obsever component */

  const prevManagerRef = useRef(null);

  // NOTE(david): We use useMemoOne instead of React's useMemo because useMemo does not guarantee memo semantics.
  // Meaning that useMemo is only a hint and JS might free the memory and rexecute the calculation
  // useMemoOne guarantees that a new manager won't be created unless there's a change in the dependencies.
  const memoizedManager = useMemoOne(() => {
    if (prevManagerRef.current) {
      prevManagerRef.current.dispose();
    }

    let newManager = null;
    if (create) {
      runInAction(() => {
        newManager = new MgrCls(parentMgr, initVars, { autoDispose: false });
        // redundant with above { autoDispose: false } argument,
        // but necessary because old code was not passing correct paramters to super
        newManager.disableAutoDispose();
      });
    }
    return newManager;
  }, [parentMgr, create, ...dependencies]);

  // Dispose manager on unmounting the component.
  useEffect(
    () => () => {
      if (memoizedManager) memoizedManager.dispose();
    },
    [],
  );

  prevManagerRef.current = memoizedManager;

  return memoizedManager;
}
